class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

const AMTRF_LEGACY_PART_NUMBER_ALIASES = new Map([
  ['AMT1820-8-S', 'AMT1820-8-S'],
  ['AMTASFSF', 'AM401097'],
  ['AMTCALSFS-A', 'AM401106'],
  ['AMTCANFG', 'AM401204'],
  ['AMTCANFNF', 'AM401186'],
  ['AMTCANFS-A', 'AM401176'],
  ['AMTCANFSF-A', 'AM401178'],
  ['AMTCANNF', 'AM401184'],
  ['AMTCASFG', 'AM401132'],
  ['AMTCASFGF', 'AM401134'],
  ['AMTCASGF', 'AM401133'],
  ['AMTPNMZF', 'AM401158'],
  ['AMTRANNF-A', 'AM401189'],
  ['AMTSFSF-D', 'AM401109'],
  ['AMTSFSF-F', 'AM401111'],
  ['AMTKFKF', 'AM401003'],
  ['AMTKFW', 'AM401361'],
  ['AMTKFWF', 'AM401362'],
  ['AMTKK', 'AM401001'],
  ['AMTKW', 'AM401359'],
  ['AMTKWF', 'AM401360'],
  ['AMTVFVF', 'AM401318'],
  ['AMTVV', 'AM401316'],
]);

window.normalizeTechnicalSearchQuery = (value) => {
  const originalQuery = String(value || '');
  const trimmedQuery = originalQuery.trim();
  const currentPartNumber = AMTRF_LEGACY_PART_NUMBER_ALIASES.get(trimmedQuery.toUpperCase());

  if (currentPartNumber) return currentPartNumber;

  const unitMatch = trimmedQuery.match(/^(\d+(?:\.\d+)?)\s*(ghz|mhz|khz|hz|mm)$/i);

  if (!unitMatch) return originalQuery;

  const normalizedUnits = {
    ghz: 'GHz',
    mhz: 'MHz',
    khz: 'kHz',
    hz: 'Hz',
    mm: 'mm',
  };

  return `${unitMatch[1]} ${normalizedUnits[unitMatch[2].toLowerCase()]}`;
};

const AMTRF_TYPE_N_ADAPTER_QUERY = 'tag:N AND product_type:Adapter';
const AMTRF_TYPE_N_ADAPTER_DISPLAY_QUERY_KEY = 'amtrf:type-n-adapter-display-query';
const AMTRF_TECHNICAL_ADAPTER_DISPLAY_QUERIES_KEY = 'amtrf:technical-adapter-display-queries';
const AMTRF_TECHNICAL_ADAPTER_QUERY_RULES = [
  {
    query: AMTRF_TYPE_N_ADAPTER_QUERY,
    phrases: [
      'n type adapter',
      'type n adapter',
      'n connector adapter',
      'type n coaxial adapter',
      'n coaxial adapter',
    ],
  },
  {
    query: 'tag:SMA AND product_type:Adapter',
    phrases: ['sma adapter', 'sma coaxial adapter', 'sma connector adapter'],
  },
  {
    query: 'tag:TNC AND product_type:Adapter',
    phrases: ['tnc adapter', 'tnc coaxial adapter', 'tnc connector adapter'],
  },
  {
    query: 'tag:TNCA AND product_type:Adapter',
    phrases: ['tnca adapter', 'tnca coaxial adapter', 'tnca connector adapter'],
  },
  {
    query: 'tag:"2.92 mm" AND product_type:Adapter',
    phrases: ['2.92 mm adapter', 'k connector adapter', 'k adapter'],
  },
  {
    query: 'tag:"3.5 mm" AND product_type:Adapter',
    phrases: ['3.5 mm adapter'],
  },
  {
    query: 'tag:"2.4 mm" AND product_type:Adapter',
    phrases: ['2.4 mm adapter'],
  },
  {
    query: 'tag:"1.85 mm" AND product_type:Adapter',
    phrases: ['1.85 mm adapter', 'v connector adapter'],
  },
  {
    query: 'tag:"7/16" AND product_type:Adapter',
    phrases: ['7/16 adapter', '7/16 din adapter', 'din 7/16 adapter'],
  },
  {
    query: 'tag:NEX10 AND product_type:Adapter',
    phrases: ['nex10 adapter', 'n ex10 adapter'],
  },
  {
    query: 'tag:SMA AND tag:N AND product_type:Adapter',
    phrases: ['sma to n adapter', 'n to sma adapter', 'sma n adapter'],
  },
];
const AMTRF_TECHNICAL_ADAPTER_QUERY_BY_PHRASE = new Map(
  AMTRF_TECHNICAL_ADAPTER_QUERY_RULES.flatMap(({ query, phrases }) =>
    phrases.map((phrase) => [phrase, query])
  )
);
const normalizeTechnicalPhraseSeparators = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s\u2010-\u2015-]+/g, ' ')
    .replace(/\b(\d+(?:\.\d+)?)\s*mm\b/g, '$1 mm');

const normalizeAdapterPlural = (value) => value.replace(/\badapters$/, 'adapter');

const AMTRF_STRICT_SEARCH_IGNORED_WORDS = new Set([
  'a',
  'an',
  'and',
  'for',
  'of',
  'or',
  'the',
  'to',
  'with',
]);

const buildStrictSearchQuery = (value) => {
  const trimmedQuery = String(value || '').trim();
  if (!trimmedQuery || /(^|\s)[a-z][\w.]*:/i.test(trimmedQuery)) return trimmedQuery;
  if (/^\d+(?:\.\d+)?\s+(?:ghz|mhz|khz|hz|mm)$/i.test(trimmedQuery)) return trimmedQuery;

  const copiedProductId = trimmedQuery.match(/^([a-z0-9][a-z0-9./_-]*)(?:\s+\([^)]+\))$/i);
  if (copiedProductId && /\d/.test(copiedProductId[1])) return copiedProductId[1];

  const queryTokens = trimmedQuery.match(/"[^"]+"|\S+/g) || [];
  const searchableTokens = queryTokens.filter((token) => {
    const normalizedToken = token.replace(/^["']|["']$/g, '').toLowerCase();
    return normalizedToken && !AMTRF_STRICT_SEARCH_IGNORED_WORDS.has(normalizedToken);
  });

  if (searchableTokens.length < 2) return trimmedQuery;

  return searchableTokens.join(' AND ');
};

window.normalizeTechnicalSearchSubmissionQuery = (value) => {
  const unitNormalizedQuery = window.normalizeTechnicalSearchQuery(value);
  const normalizedPhrase = normalizeAdapterPlural(
    normalizeTechnicalPhraseSeparators(unitNormalizedQuery)
  );
  const scopedAdapterQuery = AMTRF_TECHNICAL_ADAPTER_QUERY_BY_PHRASE.get(normalizedPhrase);

  return scopedAdapterQuery || buildStrictSearchQuery(unitNormalizedQuery);
};

const rememberSearchDisplayQuery = (submittedQuery, originalQuery) => {
  try {
    const storedQueries = JSON.parse(
      window.sessionStorage.getItem(AMTRF_TECHNICAL_ADAPTER_DISPLAY_QUERIES_KEY) || '{}'
    );
    storedQueries[submittedQuery] = String(originalQuery || '').trim();
    window.sessionStorage.setItem(
      AMTRF_TECHNICAL_ADAPTER_DISPLAY_QUERIES_KEY,
      JSON.stringify(storedQueries)
    );

    if (submittedQuery === AMTRF_TYPE_N_ADAPTER_QUERY) {
      window.sessionStorage.setItem(
        AMTRF_TYPE_N_ADAPTER_DISPLAY_QUERY_KEY,
        String(originalQuery || '').trim()
      );
    }
  } catch (error) {
    // Search submission must continue if session storage is unavailable.
  }
};

const restoreSearchDisplayQuery = () => {
  const submittedQuery = new URLSearchParams(window.location.search).get('q');
  if (!submittedQuery) return;

  let displayQuery = '';
  try {
    const storedQueries = JSON.parse(
      window.sessionStorage.getItem(AMTRF_TECHNICAL_ADAPTER_DISPLAY_QUERIES_KEY) || '{}'
    );
    displayQuery = storedQueries[submittedQuery] || '';

    if (!displayQuery && submittedQuery === AMTRF_TYPE_N_ADAPTER_QUERY) {
      displayQuery =
        window.sessionStorage.getItem(AMTRF_TYPE_N_ADAPTER_DISPLAY_QUERY_KEY) || '';
    }
  } catch (error) {
    return;
  }

  if (!displayQuery) return;

  document.querySelectorAll('input[type="search"][name="q"]').forEach((input) => {
    input.value = displayQuery;
  });
  document.querySelectorAll('[data-search-display-query]').forEach((queryLabel) => {
    queryLabel.textContent = displayQuery;
  });
};

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest('form[role="search"]');
    const input = form?.querySelector('input[type="search"][name="q"]');
    if (!input) return;

    const originalQuery = input.value;
    const normalizedQuery = window.normalizeTechnicalSearchSubmissionQuery(originalQuery);
    if (normalizedQuery !== originalQuery) {
      rememberSearchDisplayQuery(normalizedQuery, originalQuery);
    }
    if (normalizedQuery !== input.value) input.value = normalizedQuery;
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', restoreSearchDisplayQuery);
} else {
  restoreSearchDisplayQuery();
}

window.addEventListener('pageshow', restoreSearchDisplayQuery);
